import { useQuery } from '@tanstack/react-query'
import { Link, useRouterState } from '@tanstack/react-router'
import {
  CalendarDays,
  GitBranch,
  Lock,
  type LucideIcon,
  Percent,
  ShieldCheck,
  Users2,
  Users as UsersIcon,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { api } from '../lib/api'
import { useMobilePageTitle } from './shell/mobile-shell-outlets'
import { PageHeader } from './ui/page-header'
import { TabNav, tabPillClass } from './ui/tab-nav'

interface PersonnelTab {
  to: string
  label: string
  icon: LucideIcon
  adminOnly?: boolean
}

// Order in the tab strip = order in the sidebar group: Teams first (the
// org-wide structure), then Mitarbeiter (people), then Zuschläge (rules),
// then Soll-Stunden (capacity) and Sichtbarkeit (UI permissions) which
// are more individual / less frequently touched.
const TABS: PersonnelTab[] = [
  { to: '/settings/teams', label: 'Teams', icon: Users2 },
  { to: '/settings/users', label: 'Mitarbeiter', icon: UsersIcon, adminOnly: true },
  { to: '/settings/org', label: 'Organigramm', icon: GitBranch, adminOnly: true },
  { to: '/settings/surcharges', label: 'Zuschläge', icon: Percent, adminOnly: true },
  { to: '/settings/holidays', label: 'Feiertage', icon: CalendarDays, adminOnly: true },
  { to: '/settings/blackouts', label: 'Sperrzeiten', icon: Lock, adminOnly: true },
  { to: '/settings/vacation-rules', label: 'Urlaubsregeln', icon: ShieldCheck, adminOnly: true },
]

export function PersonnelLayout({
  title,
  // Bewusst nicht mehr gerendert (Ticket „obere Leiste einreihig") —
  // analog SettingsLayout; Prop bleibt, bis die Aufrufer bereinigt sind.
  description: _description,
  children,
}: {
  title: string
  description?: string
  children: ReactNode
}) {
  const { data: meData } = useQuery({ queryKey: ['auth', 'me'], queryFn: () => api.me() })
  const location = useRouterState({ select: (s) => s.location.pathname })
  const isAdmin = meData?.user?.role === 'admin'
  // Mobile-Shell (<640): EIN Titel für alle 7 Personal-Routen — der
  // Seiten-Kontext steckt im aktiven Tab der Subnav-Zeile (bleibt im
  // Content, User-Entscheid: kein Sheet).
  useMobilePageTitle('Personalverwaltung')

  const visibleTabs = TABS.filter((t) => !t.adminOnly || isAdmin)

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Einreihiger Standard-Kopf (Ticket): Eyebrow ersetzt die alte
          Breadcrumb-Zeile, Tab-Pills in der Kopfzeile (Muster Stundenkonten).
          keep-row: Tabs sind am Phone die einzige Bereichs-Navigation. */}
      <PageHeader
        title={title}
        eyebrow="Personalverwaltung"
        phoneMode="keep-row"
        inline={
          <TabNav ariaLabel="Personalverwaltung-Bereiche">
            {visibleTabs.map((tab) => {
              const active = location === tab.to || location.startsWith(`${tab.to}/`)
              const Icon = tab.icon
              return (
                <Link key={tab.to} to={tab.to} className={tabPillClass(active)}>
                  <Icon className="h-3.5 w-3.5" strokeWidth={active ? 2.25 : 2} />
                  {tab.label}
                </Link>
              )
            })}
          </TabNav>
        }
      />

      <div>{children}</div>
    </div>
  )
}
